import { memo, useMemo } from 'react';
import { SxProps, Theme } from '@mui/material';
import classNames from 'classnames';
import { mergeSX } from '../../services/helpers';
import { S3, S4, S6 } from '../../theme';
import styles from './style.module.css';
import Grid from '../Grid';
import Card, { CardContent } from '../Card';
import Typography from '../Typography';
import SlashIcon from '../../icons/SlashIcon';
import CreditCardAvatar from '../CreditCardAvatar';
import StripeImage from '../../images/StripeImage';
import { formatCardNumber } from '../../services';

export interface ICreditCardProps {
  cardNumber?: string;
  cardHolder?: string;
  cardNumberType: string | null;
  expirationMonth?: string;
  expirationYear?: string;
  cvv: string | null;
  flip: boolean;
  width?: string | number;
  height?: string | number;
  monthTranslationKey: string;
  yearTranslationKey: string;
  onClick: () => void;
  sx?: SxProps<Theme>;
}

interface IBuildSXParams {
  width: string | number;
  height: string | number;
}

const buildSX = ({ width, height }: IBuildSXParams) => ({
  '&.MuiPaper-root': {
    width,
    height: `calc(${height}px - 32px)`, // height of card is height of container minus paddingY
    borderRadius: 4,
    paddingY: 2,
  },
  '& .MuiCardContent-root': {
    height: '100%',
    padding: 0,
  },
});

const CreditCard = (props: ICreditCardProps) => {
  const {
    flip,
    cardNumber = '',
    cardHolder = '',
    expirationMonth,
    expirationYear,
    cvv,
    width = 343,
    height = 186,
    sx,
    onClick,
    monthTranslationKey,
    yearTranslationKey,
    cardNumberType,
  } = props;
  const formattedCardNumber = useMemo(() => formatCardNumber(cardNumber), [cardNumber]);

  return (
    <Grid
      onClick={onClick}
      className={classNames(styles.creditCard, { [styles.rotate]: flip })}
      width={width}
      height={height}
    >
      <Grid className={styles.creditCardInner}>
        <Grid className={styles.creditCardFront}>
          <Card sx={mergeSX(buildSX({ width, height }), sx)}>
            <CardContent>
              <Grid
                container
                flexDirection="column"
                justifyContent="space-between"
                alignItems="center"
                height="100%"
                paddingX={2}
              >
                <Grid container>
                  <Typography variant="h6">
                    {formattedCardNumber}
                  </Typography>
                </Grid>
                <Grid container justifyContent="space-between">
                  <Grid container item xs={6}>
                    <Grid
                      item
                      container
                      flexDirection="column"
                      alignItems="center"
                      maxWidth="fit-content"
                    >
                      <Grid item>
                        <Typography variant="caption" noWrap color={expirationMonth ? S6 : S3}>
                          {monthTranslationKey}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">{expirationMonth}</Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      container
                      justifyContent="center"
                      width={18}
                    >
                      <SlashIcon color={S4} />
                    </Grid>
                    <Grid
                      item
                      container
                      flexDirection="column"
                      alignItems="center"
                      maxWidth="fit-content"
                    >
                      <Grid item>
                        <Typography variant="caption" noWrap color={expirationYear ? S6 : S3}>
                          {yearTranslationKey}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">{expirationYear}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    xs={6}
                    alignContent="center"
                    justifyContent="flex-end"
                    maxWidth="fit-content"
                  >
                    <CreditCardAvatar mr={0} type={cardNumberType} />
                  </Grid>
                </Grid>
                <Grid container>
                  <Typography variant="caption" noWrap>
                    {cardHolder}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid className={styles.creditCardBack}>
          <Card sx={mergeSX(buildSX({ width, height }), sx)}>
            <CardContent>
              <Grid>
                <StripeImage />
              </Grid>
              <Grid ml={11} mt={7}>
                <Typography>{cvv}</Typography>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default memo(CreditCard);
